export const enum LocalStorageManagerKeys {
    GB_UUID = "GB_UUID",
    EMAIL = "ZING_WEB_EMAIL",
    USER_UUID = "ZING_WEB_USER_UUID",
    IS_AUTH_STEP = "IS_AUTH_STEP",
    IS_SKIP_BODY_SCAN = "IS_SKIP_BODY_SCAN",
    IS_SKIP_NUTRITION_BOOK = "IS_SKIP_NUTRITION_BOOK",
    IS_AUTH_PASSED = "IS_AUTH_PASSED",
    IS_PAYWALL_STEP = "IS_PAYWALL_STEP",
    PAYWALL_REACHED_TIMESTAMP = "PAYWALL_REACHED_TIMESTAMP",
    ANSWERS = "ZING_ANSWERS",
    EXTERNAL_ID = "AD_SIDE_CUSTOMER_ID",
    BOUGHT_PLAN_IDENT = "ZING_BOUGHT_PLAN_IDENT",
    PAYMENT_METHOD = "ZING_PAYMENT_METHOD",
    IS_PURCHASED_WITH_BODY_SCAN = "IS_PURCHASED_WITH_BODY_SCAN",
    NAME = "ZING_USER_NAME",
    GEOLOCATION_DATA = "ZING_GEOLOCATION_DATA",
    AGE_GROUP_DECADE = "ZING_AGE_GROUP_DECADE",
    PROMO_CODE = "PROMO_CODE",
    PROMO_CODE_DATA = "PROMO_CODE_DATA",
    PROMO_CODE_RESERVE_DATA = "PROMO_CODE_RESERVE_DATA",
    IS_ADDITIONAL_DISCOUNT = "IS_ADDITIONAL_DISCOUNT",
    AGE_GROUP = "ZING_AGE_GROUP",
    IS_FIRST_VISIT_TRACKED = "ZING_IS_FIRST_VISIT_TRACKED",
    IS_AI_BODY_SCAN_PAID = "ZING_AI_BODY_SCAN_PAID",
    IS_NUTRITION_BOOK_PAID = "ZING_NUTRITION_BOOK_PAID",
    IS_SUPPLEMENTS_GUIDE_PAID = "ZING_SUPPLEMENTS_GUIDE_PAID",
    IS_LES_MILLS_PAID = "ZING_LES_MILLS_PAID",
}

const storeItem = (key: LocalStorageManagerKeys, value: unknown): void => {
    if (value === undefined) return;
    if (value) {
        try {
            const data = JSON.stringify(value);
            localStorage.setItem(key, data);
        } catch (e) {
            console.error(e);
        }
    } else {
        localStorage.removeItem(key);
    }
};

const removeItem = (key: LocalStorageManagerKeys): void => {
    localStorage.removeItem(key);
};

const getStoredItem = <T>(key: LocalStorageManagerKeys): T | null => {
    const data = localStorage.getItem(key);
    if (data) {
        try {
            return JSON.parse(data) as T;
        } catch (e) {
            console.error(e);
        }
    }
    return null;
};

/**
 * Check if user should be redirected to paywall step
 * Returns true if user reached paywall within the TTL (time to live) period
 * Handles migration from old IS_PAYWALL_STEP boolean flag
 * @returns true if user should be redirected to paywall, false otherwise
 */
export const checkIsPaywallStep = (): boolean => {
    // Migration: Remove old boolean flag if it exists
    const oldFlag = localStorageManager.getStoredItem<boolean>(
        LocalStorageManagerKeys.IS_PAYWALL_STEP
    );
    if (oldFlag !== null) {
        localStorageManager.removeItem(LocalStorageManagerKeys.IS_PAYWALL_STEP);
    }

    // Check timestamp
    const timestamp = localStorageManager.getStoredItem<number>(
        LocalStorageManagerKeys.PAYWALL_REACHED_TIMESTAMP
    );

    if (!timestamp) {
        return false;
    }

    const now = Date.now();
    const timeDifference = now - timestamp;

    const PAYWALL_REDIRECT_TTL_MS = 48 * 60 * 60 * 1000; // (48 hours)

    // If TTL has expired, clear the timestamp and return false
    if (timeDifference > PAYWALL_REDIRECT_TTL_MS) {
        localStorageManager.removeItem(LocalStorageManagerKeys.PAYWALL_REACHED_TIMESTAMP);
        return false;
    }

    // Timestamp is valid (within TTL period)
    return true;
};

export const setPaywallReachedTimestamp = (): void => {
    const now = Date.now();
    localStorageManager.storeItem(LocalStorageManagerKeys.PAYWALL_REACHED_TIMESTAMP, now);
};

export const localStorageManager = {
    storeItem,
    removeItem,
    getStoredItem,
};
